import axios from "axios"

function subscribeToNotifications(routeParams, { expo_token }) {
    return axios.post("api/exponent/devices/subscribe", {
        expo_token: expo_token,
    })
}

function unsubscribeFromNotifications(routeParams, { expo_token }) {
    return axios.post("api/exponent/devices/unsubscribe", {
        expo_token: expo_token,
    })
}

function getCsrfToken() {
    return axios.get("sanctum/csrf-cookie")
}

function getSanctumToken(routeParams, values) {
    return axios.post("api/sanctum/token", {
        email: values.email,
        password: values.password,
        device_name: values.device_name,
    })
}

// « Recevoir un code » — the answer is the same whether an account matches or not.
function requestLoginCode(routeParams, { identifier }) {
    return axios.post("api/v1/login-code/request", {
        identifier,
        origin: "app",
    })
}

// Same payload as getSanctumToken, plus `families` and `method`.
function verifyLoginCode(routeParams, { identifier, code, device_name }) {
    return axios.post("api/v1/login-code/verify", {
        identifier,
        code,
        device_name,
    })
}

function redeemLoginLink(routeParams, { token, device_name }) {
    return axios.post("api/v1/login-link", { token, device_name })
}

function onboardingViewed() {
    return axios.post("api/user/onboarding-viewed")
}

function sendMessageToTutor({ id }, formData, urlParams, onUploadProgress) {
    return axios.post(`api/v1/user/send-message-to-tutor`, formData, {
        onUploadProgress,
    })
}

function championIntroViewed({}, data) {
    return axios.post("api/user/champion-intro-viewed", data)
}

function dismissProfileCustomization() {
    return axios.post("api/user/dismiss-profile-customization")
}

function registerAndGetSanctumToken(routeParams, values) {
    return axios.post("api/v1/register/token", values)
}

function registerV2(routeParams, values) {
    return axios.post("api/v2/register", values)
}

function registerTrialV2(routeParams, values) {
    return axios.post("api/v2/register-trial", values)
}

function registerSpecialV2(routeParams, values) {
    return axios.post("api/v2/register-special", values)
}

function registerWorkshopV2(routeParams, values) {
    return axios.post("api/v2/register-workshop", values)
}

function revokeSanctumToken() {
    return axios.post("api/sanctum/token/revoke", {})
}

function login(routeParams, values) {
    return axios
        .post("api/login", {
            email: values.email,
            password: values.password,
            remember: values.remember,
        })
        .then((response) => {
            return response
        })
}

function logout() {
    return axios.post("api/logout").then((data) => {
        return data
    })
}

function register(routeParams, values) {
    return axios.post("api/register", values).then((response) => {
        return response
    })
}

function checkEmailAddress({}, values) {
    return axios.post("api/check-email-address", values)
}

function checkEmailAddressAuth({}, values) {
    return axios.post("api/check-email-address-auth", values)
}

function emailVerify({ id, hash }, data, params) {
    return axios.get(`api/email/verify/${id}/${hash}`, { params })
}

function emailResend() {
    return axios.post("api/email/resend")
}

function sendResetPasswordLink(routeParams, { email }) {
    return axios.post("api/password/email", {
        email,
    })
}

function resetPassword(routeParams, values) {
    return axios.post("api/password/reset", values)
}

function updateUser(routeParams, values) {
    return axios
        .post(`api/user?_method=PUT`, values)

        .then((response) => {
            return response
        })
}

function updateStudentAccount(routeParams, values) {
    return axios.put(`api/user/student-account`, values)
}

function updateTutorAccount(routeParams, values) {
    return axios.put(`api/user/tutor-account`, values)
}

function getUser({}, {}, urlParams = {}) {
    return axios.get(`api/user`, { params: urlParams })
}

function updateProfile(routeParams, values, urlParams, onUploadProgress) {
    return axios.post(`api/user/profile?_method=PUT`, values, {
        headers: {
            "Content-Type": "multipart/form-data",
        },
        onUploadProgress,
    })
}

function updateUserProfile(routeParams, values) {
    return axios.put(`api/user/profile`, values).then((response) => {
        return response
    })
}

// La fiche « Ma lecture » : le serveur renvoie la lecture à appliquer, déjà résolue.
function updateReadingSettings(routeParams, values) {
    // Le backend d'avant le retrait de « une question à la fois » exige encore la clé, et le front
    // et le backend ne se déploient pas ensemble : l'envoyer éteinte évite un 422 entre les deux.
    // À retirer avec le `one_question: false` que sert `StudentProfile::readingSettings()`.
    return axios.put("api/v1/user/reading-settings", {
        ...values,
        one_question: false,
    })
}

/**
 * Fuseau du compte : `{ auto: true }` suit l'appareil (le serveur prend aussitôt le fuseau de
 * l'en-tête `X-Device-Timezone`), `{ auto: false, timezone }` fixe un fuseau IANA choisi.
 * Rend le `UserResource` à jour.
 */
function updateTimezone(routeParams, values) {
    return axios.put("api/user/timezone", values)
}

/** La liste des fuseaux qu'on peut choisir : `[{ id: "Asia/Dubai", label: "Asie/Dubaï" }, …]`. */
function getTimezones() {
    return axios.get("api/timezones")
}

function updatePassword(routeParams, values) {
    return axios.put("api/user/password", values)
}

function updateAvatarSelected(routeParams, values) {
    return axios.post("api/user/avatar-selected?_method=PUT", values)
}

function deleteAccount() {
    return axios.delete("api/user")
}

function updateTitleSkillSet(routeParams, values) {
    return axios.put("api/v1/user/title-skill-set", values)
}

function updateSlots(routeParams, values) {
    return axios.put("/api/user/interview-slots", values)
}

function updateStudentSlot({ slotId }, values) {
    return axios.put(`/api/user/interview-slots/${slotId}`, values)
}

function updateEmail({}, values) {
    return axios.put(`/api/user/email`, values)
}

function hasMobileSession() {
    return axios.get(`/api/user/has-mobile-session`)
}

function getAvailableTrialLiveSessions() {
    return axios.get(`/api/v1/user/trial-slots`)
}

function postNewTrialLiveSessionChoice({}, values) {
    return axios.post(`/api/v1/user/trial-slots`, values)
}

function postConfirmTrial({}, values) {
    return axios.post(`/api/v1/user/confirm-trial`, values)
}

function postPrioritizedSchoolSubjects({}, values) {
    return axios.post(
        `/api/v1/user/prioritized-school-subjects?_method=PUT`,
        values
    )
}

function postStudentEvaluation({}, values) {
    return axios.post(`/api/v1/user/student-evaluation`, values)
}

function postPrepareForTrial({}, values) {
    return axios.post(`/api/v1/user/prepare-for-trial`, values)
}

function validateToken() {
    return axios.get(`/api/sanctum/token/validate`)
}

function getFamilyTokens(routeParams, values) {
    return axios.post("api/sanctum/family-tokens", {
        device_name: values.device_name,
    })
}

function updateBillingAddress({}, values, queryParams) {
    return axios.put("api/v2/user/billing-address", values, {
        params: queryParams,
    })
}

function bootstrapImpersonation({}, { code }) {
    return axios.post("api/v1/impersonation/bootstrap", { code })
}

function exitImpersonation() {
    return axios.post("api/v1/impersonation/exit", {})
}

export const authService = {
    updateSlots,
    updateReadingSettings,
    championIntroViewed,
    subscribeToNotifications,
    unsubscribeFromNotifications,
    getCsrfToken,
    login,
    logout,
    register,
    checkEmailAddress,
    checkEmailAddressAuth,
    emailVerify,
    emailResend,
    sendResetPasswordLink,
    resetPassword,
    updateUser,
    updateProfile,
    updateUserProfile,
    updateTimezone,
    getTimezones,
    updatePassword,
    updateStudentAccount,
    updateTutorAccount,
    updateAvatarSelected,
    getSanctumToken,
    requestLoginCode,
    verifyLoginCode,
    redeemLoginLink,
    registerAndGetSanctumToken,
    registerV2,
    registerTrialV2,
    registerSpecialV2,
    registerWorkshopV2,
    revokeSanctumToken,
    getUser,
    onboardingViewed,
    deleteAccount,
    updateTitleSkillSet,
    updateStudentSlot,
    updateEmail,
    getAvailableTrialLiveSessions,
    postNewTrialLiveSessionChoice,
    postConfirmTrial,
    postPrioritizedSchoolSubjects,
    postStudentEvaluation,
    postPrepareForTrial,
    hasMobileSession,
    sendMessageToTutor,
    validateToken,
    getFamilyTokens,
    dismissProfileCustomization,
    updateBillingAddress,
    bootstrapImpersonation,
    exitImpersonation,
}
